/**
 * Landing page. Two audiences, one page: organizers who need live scoring, and
 * sponsors who want the attention it generates. Both CTAs are above the fold.
 */
import { Link } from 'react-router-dom';
import { AD_ZONES, formatCentsCompact } from '@fairwaylive/shared';
import { AdProvider } from '../lib/adClient.tsx';
import { AdZone } from '../components/AdZone.tsx';
import { HeroCarousel, Section } from '../components/Shell.tsx';
import { useFetch } from '../lib/useLive.ts';

interface FormatsResponse {
  formats: Array<{ key: string; label: string; category: string; tier: string; description: string }>;
  count: number;
}

export function LandingPage() {
  const { data: formats } = useFetch<FormatsResponse>('/api/catalogue/formats');

  return (
    <AdProvider surface="landing" allowInterstitial={false}>
      <HeroCarousel height="h-[540px]">
        <div className="mx-auto w-full max-w-[1240px] px-4 pb-12">
          <p className="eyebrow text-[var(--color-sand)]">Live scoring · South African golf</p>
          <h1 className="board-title mt-2 max-w-[20ch] text-[42px] font-bold leading-[0.95] sm:text-[68px]">
            The board your clubhouse actually watches
          </h1>
          <p className="mt-4 max-w-[54ch] text-base text-[var(--color-sand)] sm:text-lg">
            Hole-by-hole scoring from the course, {formats?.count ?? 20}+ competition formats, gross, net and team boards
            running at once, and a big-screen display built for the bar TV.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/demo" className="btn btn-primary !px-6 !py-3 !text-base">
              Watch the live demo
            </Link>
            <Link to="/pricing" className="btn btn-ghost !px-6 !py-3 !text-base">
              Run your first event free
            </Link>
            <Link to="/rate-card" className="btn btn-gold !px-6 !py-3 !text-base">
              Advertise on it
            </Link>
          </div>
        </div>
      </HeroCarousel>

      <div className="mx-auto max-w-[1240px] px-4 pt-6">
        <AdZone zoneKey="top_banner" hideWhenEmpty={false} />
      </div>

      <Section eyebrow="Built for the way clubs actually play" title="Everything the incumbent does, plus the parts it skips">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            {
              title: 'A real format engine',
              body: `${formats?.count ?? 22} formats out the box — medal, Stableford, quota, par/bogey, four-ball better-ball, alternate shot, Chapman, scramble, skins, eclectic, knockout, league and order of merit. New formats plug in without touching scoring logic.`,
            },
            {
              title: 'Many boards, one event',
              body: 'Gross, net, team and division boards computed from the same cards, each with its own format and count-out rules. Every board is live at once.',
            },
            {
              title: 'Mobile scoring that works on the course',
              body: 'Individual or whole four-ball entry, marker sign-off, shots-received dots on the card, and putts, fairways and GIR captured if you want the stats.',
            },
            {
              title: 'Clubhouse big screen',
              body: 'Fullscreen auto-rotating display for the TV, paging through the field with a sponsor card in every cycle. Cast it and leave it.',
            },
            {
              title: 'Playing Ability index',
              body: 'A WHS-style index maintained automatically from submitted cards, with soft and hard caps — plus an optional hook to your national body’s API.',
            },
            {
              title: 'GPS rangefinder',
              body: 'Distance to pin, green front and back, hazard carries and layup points, behind a clean provider interface ready for licensed course data.',
            },
          ].map((feature) => (
            <article key={feature.title} className="card p-5">
              <h3 className="board-title mb-2 text-lg font-bold text-[var(--color-fairway-300)]">{feature.title}</h3>
              <p className="text-sm leading-relaxed text-[var(--color-muted)]">{feature.body}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section eyebrow="For sponsors" title="Golf attention, measured and sold by the placement">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <p className="max-w-[62ch] text-[15px] text-[var(--color-muted)]">
              A field of 100 refreshes the board a dozen times a round, and the clubhouse screen runs all afternoon.
              Nine placement types are sold self-serve — flat monthly sponsorship or CPM — with impression, click and
              dwell reporting per creative.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {AD_ZONES.filter((z) => z.indicativeFlatCentsMonthly > 0)
                .slice(0, 6)
                .map((zone) => (
                  <div key={zone.key} className="flex items-baseline justify-between gap-4 rounded-[10px] border border-[color-mix(in_oklab,var(--color-fairway-400)_18%,transparent)] px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold">{zone.label}</p>
                      <p className="text-[11px] text-[var(--color-muted-dim)]">
                        {zone.size.w}×{zone.size.h} · {zone.kind}
                      </p>
                    </div>
                    <p className="tabular whitespace-nowrap text-sm font-bold text-[var(--color-gold-400)]">
                      {formatCentsCompact(zone.indicativeFlatCentsMonthly)}
                      <span className="text-[10px] font-normal text-[var(--color-muted-dim)]">/mo</span>
                    </p>
                  </div>
                ))}
            </div>
            <Link to="/rate-card" className="btn btn-gold mt-5">
              Full rate card and availability
            </Link>
          </div>
          <aside className="space-y-4">
            <AdZone zoneKey="sidebar_primary" hideWhenEmpty={false} />
            <AdZone zoneKey="affiliate_slot" hideWhenEmpty={false} />
          </aside>
        </div>
      </Section>

      {formats ? (
        <Section id="formats" eyebrow="Format engine" title={`${formats.count} scoring formats, pluggable`}>
          <div className="flex flex-wrap gap-2">
            {formats.formats.map((format) => (
              <span
                key={format.key}
                title={format.description}
                className={`rounded-full border px-3 py-1.5 text-[13px] ${
                  format.tier === 'core'
                    ? 'border-[var(--color-fairway-300)] text-[var(--color-fairway-300)]'
                    : 'border-[color-mix(in_oklab,var(--color-fairway-400)_28%,transparent)] text-[var(--color-muted)]'
                }`}
              >
                {format.label}
              </span>
            ))}
          </div>
          <p className="mt-4 text-xs text-[var(--color-muted-dim)]">
            Outlined in green: included on the free plan. The rest unlock on Club and above.
          </p>
        </Section>
      ) : null}

      <Section>
        <div className="card flex flex-col items-start justify-between gap-6 bg-gradient-to-br from-[var(--color-fairway-500)] to-[var(--color-fairway-800)] p-8 sm:flex-row sm:items-center">
          <div>
            <h2 className="board-title text-[26px] font-bold sm:text-[32px]">Put your medal on the board this Saturday</h2>
            <p className="mt-2 max-w-[52ch] text-sm text-[var(--color-sand)]">
              One event, 40 players, live board and big-screen mode — free, ads on. Upgrade when the society calendar
              outgrows it.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/pricing" className="btn btn-primary !px-6 !py-3">
              Start free
            </Link>
            <Link to="/demo" className="btn btn-ghost !px-6 !py-3">
              See it live
            </Link>
          </div>
        </div>
      </Section>
    </AdProvider>
  );
}
